import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Switch } from "../../../../../../../components/ui/switch";
import BaseForm, { Field } from "../../../../../../../components/shared/Form";
import DateInput from "../../../../../../../components/shared/inputs/DateInput";
import { Input } from "../../../../../../../components/ui/input";
import ResourceSelect from "../../../../../../../components/shared/inputs/ResourceSelect";
import WeekdaySelect from "../../../../../../../components/shared/inputs/WeekdaySelect";
import DoesNotRepeat from "./components/DoesNotRepeat";
import Actions from "./components/Actions";
import Breaks from "./Simple/Breaks";
import Simple from "./Simple";
import Advanced from "./Advanced";
import EventResults from "../../../modals/EventResults";
import { createEventSchema } from "./zodValidation";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { useSelectTemplate } from "./components/SelectTemplate/useSelectTemplate";
import { useFormActions } from "../../../../hooks/form/useFormActions";
import { useInitialValues } from "../../../../hooks/form/useInitialValues";
import { useFormSync } from "../../../../hooks/form/useFormSync";
import { useIsSimpleToggle } from "../../../../hooks/form/useIsSimpleToggle";
import { useDateFilters } from "../../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useGroups } from "../../../../../../Settings/General/OrganisationLevelGroups/context";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { formatParams } from "../../../../../../../utils/api/format";
import type { Event, Section } from "../../../../types/Event";
import type { UnavailableBlock } from "../../../../helpers/data/calculateUnavailableEvents";
import type { SelectedCellOrEvent } from "../../../../types/ScheduleState";
import type { Interval } from "../../../../types/Resource";
import type { FilterRow } from "../../../../../../../components/shared/filters/Filters/types";

const getScheduleUserSearchFilters = (search: string) => ({
    search,
    withoutLookups: "true",
});

// Shift-only filters that don't apply to /users
const NON_USER_FILTERS = ["hourType", "payCode", "status"];

// Rename schedule filters to the names the People feature sends to /users
const toPeopleFilterName = (name: string) => {
    if (name === "job") return "jobs.jobId";
    if (name.startsWith("location")) return "locations.locationId";
    if (name === "groupingHome") return "groupings";
    if (/^level\d+(Home)?$/.test(name)) return name.replace("Home", "");
    return name;
};

const toPeopleFilters = (filters: FilterRow[] = []) =>
    filters
        .filter(({ name }) => !NON_USER_FILTERS.includes(name))
        .map((filter) => ({ ...filter, name: toPeopleFilterName(filter.name) }));

interface EventFormProps {
    openDeleteModal: ({ type, event, onSuccess }: { type: string, event: Event | SelectedCellOrEvent, onSuccess?: () => void }) => void;
    close: () => void;
    intervals: UnavailableBlock[];
    intervalsHeight: number;
}

export type EventFormValues = {
    id?: string;
    date?: string | Date;
    startDate?: Date;
    endDate?: Date;
    users?: string[];
    isSimple: boolean;
    sections: Section[];
};

function Form({ close, openDeleteModal, intervals }: EventFormProps) {
    const { t } = useTranslation();
    const { mode, from, to } = useDateFilters();
    const { selectedCellOrEvent, type } = useScheduleContext();
    const { allocatedGroups } = useGroups();
    const initialValues = useInitialValues();
    const [filters] = useFilters();

    const schema = useMemo(
        () => createEventSchema({ t, intervals, mode, selectedCellOrEvent, allocatedGroups }),
        [t, intervals, mode, selectedCellOrEvent, allocatedGroups]
    );

    const form = useForm({
        defaultValues: initialValues,
        resolver: zodResolver(schema)
    });
    const isSimple = useWatch({ control: form.control, name: "isSimple" });

    useFormSync({ form, initialValues, isSimple });
    const onIsSimpleChange = useIsSimpleToggle(form);

    const {
        publish,
        saveDraft,
        saveTemplate,
        remove,
        createRequest,
        unassignedShiftSubmit,
        results,
        closeResults,
        loading
    } = useFormActions({ form, close, openDeleteModal });

    const {
        open: openSelectTemplateModal,
        modal: selectTemplateModal
    } = useSelectTemplate({ submit: saveTemplate });

    const allDayUnavailable = useMemo(
        () => intervals?.find((interval: Interval) => interval.allDay && !interval.isAvailable),
        [intervals]
    );

    const userParams = useMemo(() => formatParams({
        pageIndex: undefined,
        pageSize: undefined,
        filters: toPeopleFilters(filters?.filters)
    }), [filters?.filters]);

    return (
        <BaseForm form={form} className="tw-flex-1 tw-min-h-0">
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-pr-1">
                <Field
                    name="date"
                    label={t("shift-date")}
                    layout="row"
                    required
                >
                    {selectedCellOrEvent?.isTemplate ? (
                        <WeekdaySelect disabled={true} />
                    ) : (
                        <DateInput
                            disabled={!selectedCellOrEvent?.isUnassignedShift ||
                                (["day", "month"].includes(mode) && selectedCellOrEvent?.isUnassignedShift)
                            }
                            minDate={moment(from).toDate()}
                            maxDate={moment(to).toDate()}
                        />
                    )}
                </Field>

                {selectedCellOrEvent?.isUnassignedShift && (
                    <Field name="slots" label={t("number-of-slots")} layout="row" required>
                        <Input
                            type="number"
                            min={1}
                            step={1}
                            placeholder="1"
                        />
                    </Field>
                )}

                {(mode === "month" && selectedCellOrEvent?.type !== "unassigned" && !selectedCellOrEvent?.isUnassignedShift) && (
                    <Field name="users" label={t("user")} required layout="row">
                        <ResourceSelect
                            placeholder={t("users")}
                            mode="multiple"
                            resourcePath="/users"
                            renderLabel={renderUserName}
                            getSearchFilter={getScheduleUserSearchFilters}
                            disabled={
                                selectedCellOrEvent?.addAnotherEvent ||
                                !selectedCellOrEvent?.isCreating ||
                                ["home", "profile"].includes(type)
                            }
                            params={userParams}
                        />
                    </Field>
                )}

                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                    <Field
                        name="isSimple"
                        label={isSimple ? t("simple") : t("advanced")}
                        layout="row"
                        valuePropName="checked"
                    >
                        <Switch onCheckedChange={onIsSimpleChange} />
                    </Field>

                    {isSimple && <Breaks />}
                    {(!selectedCellOrEvent?.isUnassignedShift && !selectedCellOrEvent?.isTemplate) && (
                        <DoesNotRepeat />
                    )}
                </div>

                {isSimple ? <Simple /> : <Advanced />}
            </div>

            {!allDayUnavailable && (
                <div className="tw-shrink-0 tw-border-t tw-mt-4 tw-pt-4">
                    <Actions
                        publish={publish}
                        saveDraft={saveDraft}
                        saveTemplate={saveTemplate}
                        remove={remove}
                        createRequest={createRequest}
                        unassignedShiftSubmit={unassignedShiftSubmit}
                        close={close}
                        openSelectTemplateModal={openSelectTemplateModal}
                        loading={loading}
                    />
                </div>
            )}

            {selectTemplateModal}
            <EventResults results={results} close={closeResults} />
        </BaseForm >
    );
}

export default Form;
